// ABOUTME: Integrations page for managing external service connections (voice assistants)
// ABOUTME: Supports user-level voice integration linking and per-family activation

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  SettingsVoice as AlexaIcon,
  CheckCircle as ConnectedIcon,
  Error as DisconnectedIcon,
} from '@mui/icons-material';
import SharedAppBar from './SharedAppBar';
import VideoPlayer from './VideoPlayer';
import { familyAPI } from '../services/api';
import { UserVoiceIntegrationDto, Family } from '../types/api';
import { alexaBrandColor } from '../theme/tokens';
import naluV1Theme from '../theme/theme.nalu-v1';

const getErrorMessage = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    return err.response?.data?.message || fallback;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return fallback;
};

const IntegrationsPage: React.FC = () => {
  const theme = useTheme();
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;
  const [searchParams, setSearchParams] = useSearchParams();
  const [voiceIntegrations, setVoiceIntegrations] = useState<UserVoiceIntegrationDto[]>([]);
  const [families, setFamilies] = useState<Family[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [integrationsData, familiesData] = await Promise.all([
        familyAPI.getUserVoiceIntegrations(),
        familyAPI.getFamilies(),
      ]);

      setVoiceIntegrations(integrationsData);
      setFamilies(familiesData);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load integrations'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle OAuth completion redirect
  useEffect(() => {
    const alexaAuthSuccess = searchParams.get('alexa_auth_success');

    if (alexaAuthSuccess === 'true') {
      setSuccessMessage('Alexa account linked successfully! You can now connect it to your families below.');

      const newParams = new URLSearchParams(searchParams);
      newParams.delete('alexa_auth_success');
      setSearchParams(newParams, { replace: true });

      loadData();
    }
  }, [searchParams, setSearchParams, loadData]);

  const handleUnlink = async (integrationType: string) => {
    try {
      setActionLoading(`unlink-${integrationType}`);
      setError(null);
      await familyAPI.unlinkVoiceIntegration(integrationType);
      setSuccessMessage(`${integrationType} unlinked successfully.`);
      await loadData();
    } catch (err: unknown) {
      setError(getErrorMessage(err, `Failed to unlink ${integrationType}`));
    } finally {
      setActionLoading(null);
    }
  };

  const handleConnectFamily = async (integrationId: number, familyId: number, integrationType: string) => {
    try {
      setActionLoading(`connect-${integrationId}-${familyId}`);
      // integrationId is used for the loading key; the API resolves the integration by userId + integrationType
      setError(null);
      await familyAPI.connectVoiceIntegrationToFamily(familyId, integrationType);
      setSuccessMessage('Voice assistant connected to family.');
      await loadData();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to connect voice assistant to family'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisconnectFamily = async (integrationId: number, familyId: number) => {
    try {
      setActionLoading(`disconnect-${integrationId}-${familyId}`);
      setError(null);
      await familyAPI.disconnectVoiceIntegrationFromFamily(familyId, integrationId);
      setSuccessMessage('Voice assistant disconnected from family.');
      await loadData();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to disconnect voice assistant from family'));
    } finally {
      setActionLoading(null);
    }
  };

  const alexaIntegration = voiceIntegrations.find(v => v.integrationType === 'Alexa');
  const connectedFamilyIds = new Set(alexaIntegration?.familyConnections.map(fc => fc.familyId) ?? []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <SharedAppBar title="Integrations" />

      <Box sx={{ p: 3, flexGrow: 1 }}>
        <VideoPlayer videoFile="integrations-alexa-setup.mp4" />

        <Typography variant="h4" gutterBottom>
          External Integrations
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Connect external services to enhance your Let&apos;s Reset experience.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {/* Icon glyph sizing via fontSize, not a Typography text override */}
                  <AlexaIcon sx={{ mr: 2, fontSize: 40, color: alexaBrandColor }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6">Amazon Alexa</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Voice commands for behavior tracking
                    </Typography>
                  </Box>
                  {loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Chip
                      icon={alexaIntegration?.isLinked ? <ConnectedIcon /> : <DisconnectedIcon />}
                      label={alexaIntegration?.isLinked ? 'Linked' : 'Not Linked'}
                      color={alexaIntegration?.isLinked ? 'success' : 'default'}
                      variant="outlined"
                      size="small"
                    />
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : !alexaIntegration?.isLinked ? (
                  <>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      To enable voice commands, find and enable the &quot;Let&apos;s Reset Family Behavior Tracker&quot; skill
                      in your Alexa app, then link your account during the skill setup process.
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Supported Voice Commands:
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        <Typography component="li" variant="body2" color="text.secondary">
                          &quot;[Child] earned a [star/triangle/square]&quot;
                        </Typography>
                        <Typography component="li" variant="body2" color="text.secondary">
                          &quot;How is [Child] doing?&quot;
                        </Typography>
                      </Box>
                    </Box>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Your Alexa account is linked. Connect it to your families below to start using voice commands.
                    </Typography>
                    {alexaIntegration.linkedAt && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        Linked on {new Date(alexaIntegration.linkedAt).toLocaleDateString()}
                      </Typography>
                    )}

                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                      Family Connections
                    </Typography>

                    {families.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        You don&apos;t belong to any families yet.
                      </Typography>
                    ) : (
                      <FormGroup>
                        {families.map(family => {
                          const isConnected = connectedFamilyIds.has(family.familyId);
                          const connectKey = `connect-${alexaIntegration.id}-${family.familyId}`;
                          const disconnectKey = `disconnect-${alexaIntegration.id}-${family.familyId}`;
                          const isLoading = actionLoading === connectKey || actionLoading === disconnectKey;

                          return (
                            <FormControlLabel
                              key={family.familyId}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={isConnected}
                                  disabled={isLoading}
                                  onChange={() => isConnected
                                    ? handleDisconnectFamily(alexaIntegration.id, family.familyId)
                                    : handleConnectFamily(alexaIntegration.id, family.familyId, 'Alexa')
                                  }
                                />
                              }
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2">{family.name}</Typography>
                                  {isLoading && <CircularProgress size={12} />}
                                </Box>
                              }
                            />
                          );
                        })}
                      </FormGroup>
                    )}

                    <Divider sx={{ my: 2 }} />

                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      disabled={actionLoading === `unlink-Alexa`}
                      startIcon={actionLoading === `unlink-Alexa` ? <CircularProgress size={14} /> : null}
                      onClick={() => handleUnlink('Alexa')}
                    >
                      Unlink Alexa Account
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              elevation={1}
              // nalu-v1's flat cream fill + hairline border applies automatically via
              // MuiCard styleOverrides (issue #1900) — the dashed grey override is for other themes only
              sx={isNaluV1Theme ? {} : { bgcolor: 'grey.50', border: '1px dashed', borderColor: 'grey.300' }}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  More Integrations Coming Soon
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We&apos;re working on additional integrations to make Let&apos;s Reset even more powerful.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default IntegrationsPage;
